import React, { useContext } from 'react';
import { PortalButtonGlimmer } from './PortalGlimmerElements';
import buttonsStyles from '../../../stylesheets/components/buttons.module.scss';
import dialogStyles from '../../../stylesheets/components/dialog.module.scss';
import classNames from 'classnames';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useMediaQuery } from '@mui/material';
import { TenantPortalBodyContext } from '../tenant_portal/layout/TenantPortalBody';

type BodyStyle = React.CSSProperties;

export type PortalDialogProps = {
  dialogId: string;
  acceptFunction: () => void;
  cancelFunction?: () => void;
  title: string;
  cancelText?: string;
  submitText: string;
  bodyStyle?: BodyStyle;
  children: React.ReactNode;
  extraButton?: React.ReactNode;
  disableCancel?: boolean;
  disableSubmit?: boolean;
  rootClassName?: string;
  containerClassName?: string;
  paperClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  actionsClassName?: string;
  submitDataTestClass?: string;
  hideTitle?: boolean;
  hideActions?: boolean;
  actionsHelper?: React.ReactNode;
  mobileSheet?: boolean;
};

export const getBodyStyle = (
  mobileMedia: boolean,
  bodyStyle?: BodyStyle
): BodyStyle | undefined => {
  if (mobileMedia && bodyStyle && bodyStyle.maxHeight) {
    const newBodyStyle: BodyStyle = { ...bodyStyle };
    delete newBodyStyle.maxHeight;
    return newBodyStyle;
  } else {
    return bodyStyle;
  }
};

const PortalDialog = ({
  dialogId,
  acceptFunction,
  cancelFunction,
  title,
  cancelText,
  submitText,
  children,
  extraButton,
  disableCancel = false,
  disableSubmit = false,
  rootClassName,
  containerClassName,
  paperClassName,
  titleClassName,
  contentClassName,
  actionsClassName,
  submitDataTestClass = 'dialog-submit-button',
  hideTitle = false,
  hideActions = false,
  actionsHelper,
  mobileSheet = false,
}: PortalDialogProps) => {
  const { tenantPortalBodyRef } = useContext(TenantPortalBodyContext);
  const isMobileViewport = useMediaQuery('(max-width:600px)');
  const isMobileSheet = mobileSheet && isMobileViewport;
  const titleId = `${dialogId}-title`;

  const dialogContents = (
    <>
      {!hideTitle && (
        <DialogTitle
          id={hideTitle ? undefined : titleId}
          className={classNames(dialogStyles.title, titleClassName)}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        className={classNames(dialogStyles.content, contentClassName)}
      >
        {children}
      </DialogContent>
      {!hideActions && (
        <DialogActions
          className={classNames(dialogStyles.actions, actionsClassName)}
        >
          {extraButton}
          {cancelText && (
            <Button
              data-test-class="dialog-cancel-button"
              className={classNames(
                buttonsStyles.buttonDialogClose,
                buttonsStyles.button,
                buttonsStyles.alternate
              )}
              onClick={cancelFunction}
              disabled={disableCancel}
            >
              {cancelText}
            </Button>
          )}
          <Button
            data-test-class={submitDataTestClass}
            className={buttonsStyles.button}
            onClick={acceptFunction}
            disabled={disableSubmit}
          >
            {submitText}
          </Button>
          {actionsHelper && (
            <div className="portal-dialog-actions-helper">{actionsHelper}</div>
          )}
        </DialogActions>
      )}
    </>
  );

  if (isMobileSheet) {
    return (
      <SwipeableDrawer
        open={true}
        id={dialogId}
        anchor="bottom"
        disableSwipeToOpen
        onOpen={() => undefined}
        onClose={() => {
          if (!disableCancel && cancelFunction) cancelFunction();
        }}
        classes={{
          root: classNames(
            dialogStyles.container,
            containerClassName,
            rootClassName
          ),
          paper: classNames(dialogStyles.paper, paperClassName),
        }}
        BackdropProps={{
          classes: {
            root: dialogStyles.backdrop,
          },
        }}
        ModalProps={{
          container: tenantPortalBodyRef?.current,
        }}
        PaperProps={{
          role: 'dialog',
          'aria-modal': true,
          ...(hideTitle
            ? { 'aria-label': title }
            : { 'aria-labelledby': titleId }),
        }}
        elevation={24}
      >
        <div
          className={dialogStyles.mobileSheetHandle}
          data-test-class="mobile-sheet-handle"
          aria-hidden="true"
        />
        {dialogContents}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={true} // Figure out later
      id={dialogId}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && cancelFunction) cancelFunction();
      }}
      classes={{
        root: rootClassName,
        paper: classNames(dialogStyles.paper, paperClassName),
        container: classNames(dialogStyles.container, containerClassName),
      }}
      BackdropProps={{
        classes: {
          root: dialogStyles.backdrop,
        },
      }}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-label': hideTitle ? title : undefined,
        'aria-labelledby': hideTitle ? undefined : titleId,
      }}
      container={tenantPortalBodyRef?.current}
    >
      {dialogContents}
    </Dialog>
  );
};

interface PortalDialogGlimmerProps {
  dialogId: string;
  title: string;
  children: React.ReactNode;
  bodyStyle?: React.CSSProperties;
  extraButton?: React.ReactNode;
  containerClassName?: string;
  paperClassName?: string;
  titleClassName?: string;
  contentClassName?: string;
  actionsClassName?: string;
  cancelFunction?: () => void;
  hideTitle?: boolean;
  hideActions?: boolean;
  mobileSheet?: boolean;
}

export const PortalDialogGlimmer: React.FC<PortalDialogGlimmerProps> = ({
  dialogId,
  title,
  children,
  extraButton,
  containerClassName,
  paperClassName,
  titleClassName,
  contentClassName,
  actionsClassName,
  cancelFunction,
  hideTitle = false,
  hideActions = false,
  mobileSheet = false,
}) => {
  const { tenantPortalBodyRef } = useContext(TenantPortalBodyContext);
  const isMobileViewport = useMediaQuery('(max-width:600px)');
  const isMobileSheet = mobileSheet && isMobileViewport;
  const titleId = `${dialogId}-title`;

  const glimmerContents = (
    <>
      {!hideTitle && (
        <DialogTitle
          id={hideTitle ? undefined : titleId}
          className={classNames(dialogStyles.title, titleClassName)}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        className={classNames(dialogStyles.content, contentClassName)}
      >
        {children}
      </DialogContent>
      {!hideActions && (
        <DialogActions
          className={classNames(dialogStyles.actions, actionsClassName)}
        >
          {extraButton && <PortalButtonGlimmer />}
          <PortalButtonGlimmer />
          <PortalButtonGlimmer />
        </DialogActions>
      )}
    </>
  );

  if (isMobileSheet) {
    return (
      <SwipeableDrawer
        open={true}
        id={dialogId}
        anchor="bottom"
        disableSwipeToOpen
        onOpen={() => undefined}
        onClose={() => {
          if (cancelFunction) cancelFunction();
        }}
        classes={{
          root: classNames(dialogStyles.container, containerClassName),
          paper: classNames(dialogStyles.paper, paperClassName),
        }}
        BackdropProps={{
          classes: {
            root: dialogStyles.backdrop,
          },
        }}
        ModalProps={{
          container: tenantPortalBodyRef?.current,
        }}
        PaperProps={{
          role: 'dialog',
          'aria-modal': true,
          ...(hideTitle
            ? { 'aria-label': title }
            : { 'aria-labelledby': titleId }),
        }}
        elevation={24}
      >
        <div
          className={dialogStyles.mobileSheetHandle}
          data-test-class="mobile-sheet-handle"
          aria-hidden="true"
        />
        {glimmerContents}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={true} // Figure out later
      id={dialogId}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-label': hideTitle ? title : undefined,
        'aria-labelledby': hideTitle ? undefined : titleId,
      }}
      classes={{
        paper: classNames(dialogStyles.paper, paperClassName),
        container: classNames(dialogStyles.container, containerClassName),
      }}
      container={tenantPortalBodyRef?.current}
    >
      {glimmerContents}
    </Dialog>
  );
};

export default PortalDialog;
